import { z } from 'zod';

/**
 * Validateurs Zod réutilisables pour les formulaires du front public.
 *
 * Miroir de `apps/admin/src/lib/validation/fields.ts` et du backend
 * `apps/api/src/common/validation/patterns.ts` — garder synchronisé.
 *
 * Approche « pragmatique » : on bloque les caractères dangereux (chevrons
 * `< >` → anti-injection HTML/`<?php>`/`<script>`, caractères de contrôle)
 * partout, et on applique des formats stricts sur les champs structurés
 * (téléphone, licence). Les noms restent permissifs (accents, tirets,
 * apostrophes) pour ne pas rejeter les vrais noms.
 */

const FORBIDDEN = '<>\\u0000-\\u001F\\u007F';

export const PERSON_NAME_REGEX = /^[\p{L}\p{M}0-9 '’.-]+$/u;
export const ENTITY_NAME_REGEX = new RegExp(`^[^${FORBIDDEN}]+$`, 'u');
export const PHONE_REGEX = /^[0-9 +().-]{6,20}$/;
export const LICENSE_REGEX = /^[0-9]{1,20}$/;

const MSG = {
  personName: 'Caractères non autorisés dans ce champ',
  entityName: 'Les caractères < et > ne sont pas autorisés',
  phone: 'Numéro de téléphone invalide (chiffres, espaces, + ( ) . - uniquement)',
  license: 'Numéro de licence invalide (chiffres uniquement, sans espace)',
} as const;

/** Prénom / nom de personne, requis. */
export const personNameField = (label: string, max = 50) =>
  z
    .string()
    .trim()
    .min(1, `${label} est requis`)
    .max(max, `${label} : ${max} caractères maximum`)
    .regex(PERSON_NAME_REGEX, MSG.personName);

/** Nom d'entité (équipe, club…), requis. */
export const entityNameField = (label: string, { min = 1, max = 255 }: { min?: number; max?: number } = {}) =>
  z
    .string()
    .trim()
    .min(min, `${label} requis`)
    .max(max, `${label} : ${max} caractères maximum`)
    .regex(ENTITY_NAME_REGEX, MSG.entityName);

/** Téléphone optionnel (accepte la chaîne vide). */
export const phoneFieldOptional = z.string().trim().regex(PHONE_REGEX, MSG.phone).optional().or(z.literal(''));

/** Numéro de licence FFPJP optionnel. */
export const licenseFieldOptional = z.string().trim().regex(LICENSE_REGEX, MSG.license).optional().or(z.literal(''));
